import React, {
    ReactElement,
} from "react";
import {
    AiOutlineCloseCircle,
    AiOutlineFileAdd, AiOutlineUserAdd, AiTwotoneFilter,
} from "react-icons/ai";
import {
    BiAddToQueue, BiMessageAdd, BiNote, BiTask, BiTime,
} from "react-icons/bi";
import {
    BsAlarm, BsArchive, BsCode,
    BsFillArrowDownRightSquareFill,
    BsFillCalendar2MinusFill, BsGraphUp, BsHandThumbsUp, BsLayoutThreeColumns, BsList,
    BsPersonCheckFill, BsTags,
    BsTicketPerforatedFill,
} from "react-icons/bs";
import {
    FaPaperPlane,
    FaRobot, FaUserPlus,
} from "react-icons/fa";
import { GoIssueReopened, GoWorkflow } from "react-icons/go";
import { GrActions } from "react-icons/gr";
import { HiOutlineBookOpen, HiOutlineMail, HiShieldExclamation } from "react-icons/hi";
import { IoPersonSharp } from "react-icons/io5";
import {
    MdAvTimer,
    MdOutlineAirplaneTicket, MdOutlineAssignmentReturn, MdOutlineDynamicForm, MdOutlineFormatSize,
    MdOutlineNotStarted, MdOutlineTipsAndUpdates, MdTimerOff,
} from "react-icons/md";
import {
    RiChatNewLine,
    RiChatThreadLine,
    RiEditBoxLine, RiErrorWarningFill, RiEyeLine, RiInputCursorMove, RiLoopRightFill,
    RiP2pLine,
    RiPriceTag3Line, RiQuestionnaireFill, RiStarLine, RiTicket2Line,
    RiUserSearchLine,
} from "react-icons/ri";
import { TbCircleNumber1, TbStatusChange } from "react-icons/tb";
import { TiArrowLoop } from "react-icons/ti";
import {
    VscDebugDisconnect, VscDebugRestart, VscDiffIgnored, VscFeedback,
} from "react-icons/vsc";

import { RiChatFollowUpLine } from "@remixicon/react";
import { WorkflowService, WorkflowServiceAction } from "../../__generated__/api";
import Avatar, { AvatarVariant } from "../avatar/Avatar";

const DEFAULT_ICON = <GrActions />;

const ICON_MAP: Record<string, ReactElement> = {
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-created)": <GoWorkflow />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-creating)": <GoWorkflow />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,generic-no-ticket)": <FaPaperPlane />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,condition)": <AiTwotoneFilter />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-subtask)": <BiTask />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,assign-to-queue)": <BiAddToQueue />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,assign-to-user)": <AiOutlineUserAdd />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-finished)": <AiOutlineCloseCircle />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-hold)": <BsArchive />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-ignore)": <VscDiffIgnored />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-status-reopen)": <HiOutlineBookOpen />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-priority)": <MdOutlineAirplaneTicket />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-severity)": <RiTicket2Line />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-field-value)": <RiInputCursorMove />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,map)": <BsList />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-message)": <BiMessageAdd />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-note)": <BiNote />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-participant)": <FaUserPlus />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,trigger)": <BsFillArrowDownRightSquareFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,trigger-connector)": <VscDebugDisconnect />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-field-value)": <RiEditBoxLine />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,delay-time)": <BsFillCalendar2MinusFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-sla-tier)": <HiShieldExclamation />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,loop)": <TiArrowLoop />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,loop-multiple-choice)": <RiLoopRightFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,format)": <MdOutlineFormatSize />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-status-updated)": <MdOutlineTipsAndUpdates />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,webhook)": <BsCode />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,is-date-in-hours-of-operation)": <MdTimerOff />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-state)": <BsLayoutThreeColumns />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-field-updated)": <MdAvTimer />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-tags)": <BsTags />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-requestor)": <IoPersonSharp />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,is-user-in-group)": <BsPersonCheckFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-new-message)": <BiTime />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-due-date-expired)": <BsAlarm />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,run-deflection)": <FaRobot />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,send-feedback)": <BsHandThumbsUp />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,run-action)": <MdOutlineNotStarted />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-request-type)": <TbStatusChange />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,send-email)": <HiOutlineMail />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,allow-restart)": <VscDebugRestart />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-ticket)": <AiOutlineFileAdd />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-feedback)": <VscFeedback />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,send-question)": <RiQuestionnaireFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,fill-out-form)": <MdOutlineDynamicForm />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-status-reopened)": <GoIssueReopened />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-error)": <RiErrorWarningFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,execute-with-count)": <TbCircleNumber1 />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,on-external-ticket)": <BsTicketPerforatedFill />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,unassign)": <MdOutlineAssignmentReturn />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-metric)": <BsGraphUp />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,get-user-by-email)": <RiUserSearchLine />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,start-conversation)": <RiChatNewLine />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,channel-set-tags)": <RiPriceTag3Line />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,add-participant-thread)": <RiChatThreadLine />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,map-channel-messages)": <RiP2pLine />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,field-display)": <RiEyeLine />,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,field-required)": <RiStarLine />,

    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,automatic-followup)": <RiChatFollowUpLine />,

};

export default function WorkflowIcon({
    service,
    action,
    className,
    variant,
}: {
    service?: WorkflowService,
    action: WorkflowServiceAction,
    className?: string;
    variant?: AvatarVariant
}) {
    return service?.squareIconUrl
        ? <Avatar src={service.squareIconUrl} className={className} variant={variant} />
        : <span className={className}>{ICON_MAP[action?.id] ?? DEFAULT_ICON}</span>;
}
WorkflowIcon.defaultProps = {
    variant: "xxs",
    className: "",
    service: undefined,
};
